/**
 * External navigation helpers for iOS home-screen / standalone PWAs.
 *
 * In display-mode: standalone, navigating the top window to another origin
 * (or letting an embed do so) often opens an in-app browser sheet. Refreshing
 * that sheet can drop the Done control and leave a blank white screen with no
 * way back except force-quitting the app.
 *
 * Strategy:
 * - App-owned links: force open via a temporary <a target="_blank"> so iOS
 *   prefers Safari / the native app instead of trapping the PWA.
 * - Embeds (SoundCloud, etc.): sandbox without allow-top-navigation so the
 *   iframe cannot replace this app; popups may still open Safari / native apps.
 */

export const isStandaloneDisplayMode = () => {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(display-mode: standalone)")?.matches) {
      return true;
    }
    // Legacy iOS home-screen flag
    if (typeof navigator !== "undefined" && navigator.standalone === true) {
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
};

export const isExternalHttpUrl = (href) => {
  if (!href || typeof href !== "string") return false;
  const trimmed = href.trim();
  if (!trimmed) return false;
  // Protocol-relative or absolute http(s)
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("//")) {
    try {
      const url = new URL(trimmed, window.location.href);
      return url.origin !== window.location.origin;
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * Open an external URL outside the standalone webview when possible.
 * Safe to call for any absolute http(s) URL.
 */
export const openExternalUrl = (href) => {
  if (!href) return false;
  let absolute;
  try {
    absolute = new URL(href, window.location.href).href;
  } catch {
    return false;
  }

  // Temporary anchor is more reliable than window.open on iOS standalone
  const a = document.createElement("a");
  a.href = absolute;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  // iOS sometimes needs the node in the document
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return true;
};

/**
 * Default sandbox tokens for third-party music embeds.
 * react-iframe expects an array (it does [...sandbox].join(" ")).
 * Omits allow-top-navigation* so embeds cannot navigate the PWA shell away
 * (which triggers the iOS white in-app browser trap).
 */
export const MUSIC_EMBED_SANDBOX = [
  "allow-scripts",
  "allow-same-origin",
  "allow-popups",
  "allow-popups-to-escape-sandbox",
  "allow-forms",
  "allow-presentation",
];

/** Coerce admin string or array into tokens for react-iframe's sandbox prop */
export const toIframeSandboxList = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map(String).filter(Boolean);
  }
  if (raw == null || raw === "") return [...MUSIC_EMBED_SANDBOX];
  return String(raw)
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean);
};
